import { useEffect, useState } from "react";
import { AlertTriangle, CalendarCheck, Check, Clock, Link2, Loader, Unlink } from "lucide-react";
import { apiFetch } from "./api";

/**
 * Limits & Calendar — the practitioner's own rules about when they can be booked.
 *
 * Until now the only rule was a hidden 30-minute one, so a client could take a 7am session at
 * 6:40am or book six in a day. The numbers below start as suggestions and are theirs to change.
 * 0 always means "no limit".
 */

const STARTING = { min_notice_hours: 24, max_per_day: 4, max_days_ahead: 60 };

const FIELDS = [
  {
    key: "min_notice_hours",
    label: "Least notice you need",
    unit: "hours",
    help: "A client cannot take a session starting sooner than this. 0 means any time from now (half an hour is always kept as a minimum).",
  },
  {
    key: "max_per_day",
    label: "Most sessions in one day",
    unit: "sessions",
    help: "Once you reach this on a given day, the rest of that day disappears for clients. 0 means no cap.",
  },
  {
    key: "max_days_ahead",
    label: "How far ahead clients may book",
    unit: "days",
    help: "Nothing beyond this is offered. 0 means as far ahead as your schedule runs.",
  },
];

function CalendarRow({ provider, state, onChanged }) {
  const [busy, setBusy] = useState(false);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await apiFetch(`/calendar/connect/${provider}`);
      window.location.href = r.authorization_url;
    } catch (e) {
      alert(e.message || `${state.label} could not be started`);
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await apiFetch(`/calendar/${provider}`, { method: "DELETE" });
      onChanged();
    } finally { setBusy(false); }
  };

  return (
    <div className="vav-cal-row" data-testid={`calendar-row-${provider}`}>
      <div>
        <p className="vav-cal-name">{state.label}</p>
        {state.connected ? (
          <p className="vav-cal-sub connected" data-testid={`calendar-connected-${provider}`}>
            <Check size={13} /> Connected{state.email ? ` as ${state.email}` : ""} — your busy
            times are hidden from clients and confirmed sessions are added here
          </p>
        ) : state.needs_reconnect ? (
          <p className="vav-cal-sub warn">
            <AlertTriangle size={13} /> {state.label} has withdrawn permission. Reconnect to keep
            your calendar in step.
          </p>
        ) : state.configured ? (
          <p className="vav-cal-sub">Not connected</p>
        ) : (
          <p className="vav-cal-sub">
            Not available yet — the site owner has not finished setting up {state.label}.
          </p>
        )}
      </div>
      {state.connected ? (
        <button className="vav-cal-btn ghost" onClick={disconnect} disabled={busy}
                data-testid={`calendar-disconnect-${provider}`}>
          <Unlink size={14} /> Disconnect
        </button>
      ) : (
        <button className="vav-cal-btn" onClick={connect} disabled={busy || !state.configured}
                data-testid={`calendar-connect-${provider}`}>
          {busy ? <Loader size={14} className="spin" /> : <Link2 size={14} />}
          {state.needs_reconnect ? "Reconnect" : "Connect"}
        </button>
      )}
    </div>
  );
}

export default function SchedulingLimitsPanel({ profile, onSaved }) {
  const [limits, setLimits] = useState(STARTING);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [calendars, setCalendars] = useState(null);
  const [blockAllDay, setBlockAllDay] = useState(false);
  const [usingStartingPoints, setUsingStartingPoints] = useState(true);

  useEffect(() => {
    if (!profile) return;
    const inForce = profile.scheduling_limits || profile.scheduling_limits_in_force || STARTING;
    setLimits({
      min_notice_hours: inForce.min_notice_hours ?? STARTING.min_notice_hours,
      max_per_day: inForce.max_per_day ?? STARTING.max_per_day,
      max_days_ahead: inForce.max_days_ahead ?? STARTING.max_days_ahead,
    });
    setUsingStartingPoints(!profile.scheduling_limits);
    setBlockAllDay(!!profile.calendar_block_all_day);
  }, [profile]);

  const loadCalendars = () => apiFetch("/calendar/status").then(setCalendars).catch(() => {});
  useEffect(() => { loadCalendars(); }, []);

  // Coming back from Google or Microsoft's consent screen.
  useEffect(() => {
    const result = new URLSearchParams(window.location.search).get("calendar");
    if (!result) return;
    if (result === "connected") loadCalendars();
    if (result === "failed" || result === "expired") {
      setError("That calendar did not finish connecting. Please try again.");
    }
  }, []);

  const saveAllDay = async (next) => {
    setBlockAllDay(next);
    try {
      const updated = await apiFetch("/practitioner/booking-profile", {
        method: "PUT",
        body: JSON.stringify({ calendar_block_all_day: next }),
      });
      onSaved?.(updated);
    } catch {
      setBlockAllDay(!next);
      setError("That setting could not be saved.");
    }
  };

  const setField = (key, raw) => {
    const n = raw === "" ? "" : Math.max(0, parseInt(raw, 10) || 0);
    setLimits(l => ({ ...l, [key]: n }));
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      const payload = Object.fromEntries(
        FIELDS.map(f => [f.key, limits[f.key] === "" ? 0 : limits[f.key]]));
      const updated = await apiFetch("/practitioner/booking-profile", {
        method: "PUT",
        body: JSON.stringify({ scheduling_limits: payload }),
      });
      setUsingStartingPoints(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onSaved?.(updated);
    } catch (e) {
      setError(e.message || "Your limits could not be saved.");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="scheduling-limits-panel">
      <style>{`
        .vav-lim-grid { display: grid; gap: 18px; margin-top: 18px; }
        .vav-lim-row { display: grid; grid-template-columns: 1fr 160px; gap: 16px;
          align-items: start; padding-bottom: 16px; border-bottom: 1px solid rgba(0,0,0,0.08); }
        .vav-lim-row:last-child { border-bottom: none; }
        .vav-lim-label { font-size: 14px; font-weight: 600; color: #2b2b2b; margin: 0 0 4px; }
        .vav-lim-help { font-size: 12px; color: #6b6b6b; margin: 0; line-height: 1.6; }
        .vav-lim-input { display: flex; align-items: center; gap: 8px; }
        .vav-lim-input input { width: 78px; padding: 9px 10px; border-radius: 8px;
          border: 1px solid rgba(0,0,0,0.16); font-family: inherit; font-size: 14px;
          text-align: center; color: #2b2b2b; }
        .vav-lim-unit { font-size: 12px; color: #6b6b6b; }
        .vav-lim-note { display: flex; gap: 8px; align-items: flex-start; font-size: 12.5px;
          line-height: 1.6; color: #7a5200; background: #fdf6e3;
          border: 1px solid #efdca8; border-radius: 10px; padding: 12px 14px; }
        .vav-cal-row { display: flex; justify-content: space-between; align-items: center;
          gap: 16px; padding: 14px 0; border-bottom: 1px solid rgba(0,0,0,0.08); }
        .vav-cal-row:last-child { border-bottom: none; }
        .vav-cal-name { font-size: 14px; font-weight: 600; color: #2b2b2b; margin: 0 0 3px; }
        .vav-cal-sub { font-size: 12px; color: #6b6b6b; margin: 0; line-height: 1.6;
          display: flex; align-items: flex-start; gap: 6px; max-width: 560px; }
        .vav-cal-sub.connected { color: #1b7a4b; }
        .vav-cal-sub.warn { color: #9a5b00; }
        /* Explicit colours, not inherited CSS variables: this panel is mounted inside a
           different style scope and the Save button came out white-on-white. */
        .vav-cal-btn { display: inline-flex; align-items: center; gap: 7px; padding: 10px 18px;
          border-radius: 9px; border: none; background: #1a4a3a; color: #fff;
          font-family: inherit; font-size: 13px; font-weight: 600; cursor: pointer;
          white-space: nowrap; transition: background-color .18s ease, opacity .18s ease; }
        .vav-cal-btn:hover:not(:disabled) { background: #246b53; }
        .vav-cal-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .vav-cal-btn.ghost { background: transparent; color: #3a3a3a;
          border: 1px solid rgba(0,0,0,0.14); }
        .vav-cal-btn.ghost:hover:not(:disabled) { background: rgba(0,0,0,0.04); }
        .vav-allday { display: flex; gap: 10px; align-items: flex-start; margin-top: 18px;
          padding: 14px 16px; border: 1px solid rgba(0,0,0,0.1); border-radius: 10px;
          background: #fafaf8; font-size: 12.5px; line-height: 1.65; color: #4a4a4a;
          cursor: pointer; }
        .vav-allday input { margin-top: 2px; width: 15px; height: 15px; flex-shrink: 0;
          cursor: pointer; accent-color: #1a4a3a; }
        .vav-allday strong { color: #2b2b2b; }
        .spin { animation: vavspin 1s linear infinite; }
        @keyframes vavspin { to { transform: rotate(360deg); } }
      `}</style>

      <h2 className="vav-section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Clock size={17} /> Booking limits
      </h2>
      <p className="vav-section-sub">
        Your rules about when clients can take your time. These apply everywhere — your page here,
        your public page and anyone booking through a link.
      </p>

      {usingStartingPoints && (
        <div className="vav-lim-note" style={{ marginTop: 14 }} data-testid="limits-starting-note">
          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            These are the starting points everyone begins with. Change them to suit how you work
            and press Save — they are yours from then on.
          </span>
        </div>
      )}

      <div className="vav-lim-grid">
        {FIELDS.map(f => (
          <div className="vav-lim-row" key={f.key}>
            <div>
              <p className="vav-lim-label">{f.label}</p>
              <p className="vav-lim-help">{f.help}</p>
            </div>
            <div className="vav-lim-input">
              <input
                type="number" min="0" value={limits[f.key]}
                onChange={e => setField(f.key, e.target.value)}
                data-testid={`limit-input-${f.key}`}
              />
              <span className="vav-lim-unit">{f.unit}</span>
            </div>
          </div>
        ))}
      </div>

      {error && (
        <p style={{ color: "#b4242a", fontSize: 13, marginTop: 14 }} data-testid="limits-error">
          {error}
        </p>
      )}

      <button className="vav-cal-btn" onClick={save} disabled={saving}
              style={{ marginTop: 18 }} data-testid="limits-save">
        {saving ? <Loader size={14} className="spin" /> : <Check size={14} />}
        {saved ? "Saved" : "Save limits"}
      </button>

      <h2 className="vav-section-title"
          style={{ marginTop: 40, display: "flex", alignItems: "center", gap: 8 }}>
        <CalendarCheck size={17} /> Your own calendar
      </h2>
      <p className="vav-section-sub">
        Connect the calendar you already live by. Anything in it hides those times from clients
        here, and every session you confirm is added to it.
      </p>

      <div style={{ marginTop: 14 }}>
        {!calendars ? (
          <p className="vav-cal-sub">Checking…</p>
        ) : (
          Object.entries(calendars).map(([provider, state]) => (
            <CalendarRow key={provider} provider={provider} state={state}
                         onChanged={loadCalendars} />
          ))
        )}
      </div>

      <label className="vav-allday" data-testid="calendar-all-day-toggle">
        <input type="checkbox" checked={blockAllDay}
               onChange={e => saveAllDay(e.target.checked)}
               data-testid="calendar-all-day-checkbox" />
        <span>
          <strong>Also close my diary for all-day events.</strong> Off by default, and most
          people should leave it off. All-day and multi-day entries are usually a holiday, a
          trip or something you do alongside your day — house-sitting, a conference, a child at
          home — not a reason nobody can book you. With this off, only events with an actual
          start and finish time hide those hours, and anything running longer than a day is
          ignored. Use <strong>Blocked Dates</strong> when you genuinely want a day closed.
        </span>
      </label>
    </div>
  );
}
